import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import messages from "../../../../assets/locale/messages";
import useDebounce from "../../../../hooks/useDebounce";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  extractAdminPortalList,
  extractAdminPortalMessage,
  useAdminModulePermission,
} from "../../../../utils/adminPortalHelpers";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import {
  deleteAdminCompanyRole,
  getAdminCompanyRoles,
  updateAdminCompanyRole,
} from "../../../../network/apis/adminCompanyRoles";
import {
  ROLE_STATUS,
  buildCompanyRolesListParams,
  getCompanyRolesListMeta,
  normalizeCompanyRoles,
} from "../../../Company-portal/TeamsPermissions/roleMappers";
import { UI_TABLE_DEFAULT_PAGE_SIZE } from "../../../../utils/tablePageSize";
import {
  adminCompanyTeamRoutes,
  resolveTeamSubtabFromSearch,
} from "../adminCompanyTeamRoutes";

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const useAdminCompanyRolesList = (companyId) => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const copy = messages[lang].teamManagement;
  const rolesCopy = copy.roles;
  const { shared } = messages[lang];
  const { canAdd, canEdit, canDelete } = useAdminModulePermission(
    companyPermissionModules.rolesManagement,
  );

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(UI_TABLE_DEFAULT_PAGE_SIZE);
  const [meta, setMeta] = useState({ currentPage: 1, count: 0, pages: 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const listParams = useMemo(
    () =>
      buildCompanyRolesListParams({
        search: debouncedSearch,
        page,
        pageSize,
      }),
    [debouncedSearch, page, pageSize],
  );

  const fetchRoles = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    try {
      const response = await getAdminCompanyRoles({
        ...listParams,
        company_id: companyId,
      });
      const list = extractAdminPortalList(response);
      setRows(normalizeCompanyRoles(list));
      setMeta(getCompanyRolesListMeta(response));
    } catch (error) {
      setRows([]);
      setMeta({ currentPage: 1, count: 0, pages: 1 });
      showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, listParams, shared.generalErrMsg]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles, lang]);

  const total = meta.count || 0;
  const pages = meta.pages || Math.max(Math.ceil(total / pageSize), 1);
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  const handleSearchChange = useCallback((event) => {
    setSearch(event.target.value);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const handlePageSizeChange = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const handleToggleStatus = useCallback(
    async (role, checked) => {
      const previousActive = role.active;
      const nextStatus = checked ? ROLE_STATUS.enabled : ROLE_STATUS.disabled;
      const prevStatus = previousActive
        ? ROLE_STATUS.enabled
        : ROLE_STATUS.disabled;

      setRows((current) =>
        current.map((item) =>
          item.id === role.id
            ? { ...item, active: checked, status: nextStatus }
            : item,
        ),
      );

      try {
        await updateAdminCompanyRole({
          id: role.id,
          data: { status: nextStatus },
        });
        showSuccessMsg(messages[lang].successMsgs.updatedSuccessfully, true);
      } catch (error) {
        setRows((current) =>
          current.map((item) =>
            item.id === role.id
              ? { ...item, active: previousActive, status: prevStatus }
              : item,
          ),
        );
        showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
      }
    },
    [lang, shared.generalErrMsg],
  );

  const handleCreateRole = useCallback(() => {
    if (!companyId) return;
    history.push(adminCompanyTeamRoutes.roleCreate(companyId));
  }, [history, companyId]);

  const handleViewRole = useCallback(
    (role) => {
      if (!role?.id || !companyId) return;
      const returnSubtab = resolveTeamSubtabFromSearch(location.search);
      history.push({
        pathname: adminCompanyTeamRoutes.roleView(companyId, role.id),
        state: { companyId, returnSubtab },
      });
    },
    [history, companyId, location.search],
  );

  const handleEditRole = useCallback(
    (role) => {
      if (!role?.id || !companyId) return;
      const returnSubtab = resolveTeamSubtabFromSearch(location.search);
      history.push({
        pathname: adminCompanyTeamRoutes.roleView(companyId, role.id),
        state: { startInEdit: true, companyId, returnSubtab },
      });
    },
    [history, companyId, location.search],
  );

  const handleDeleteRole = useCallback((role) => {
    setRoleToDelete(role);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (deleting) return;
    setRoleToDelete(null);
  }, [deleting]);

  const confirmDeleteRole = useCallback(async () => {
    if (!roleToDelete || roleToDelete.usersCount > 0 || deleting) return;

    setDeleting(true);
    try {
      const response = await deleteAdminCompanyRole(roleToDelete.id);
      const message = extractAdminPortalMessage(response);
      if (message) showSuccessMsg(message, true);
      else showSuccessMsg("deletedSuccessfully");
      setRoleToDelete(null);
      fetchRoles();
    } catch (error) {
      showErrorMsg(error?.response?.data?.message || shared.generalErrMsg);
    } finally {
      setDeleting(false);
    }
  }, [roleToDelete, deleting, fetchRoles, shared.generalErrMsg]);

  return {
    copy,
    rolesCopy,
    search,
    debouncedSearch,
    rows,
    page,
    pageSize,
    pages,
    rangeFrom,
    rangeTo,
    total,
    isLoading,
    deleting,
    roleToDelete,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    canAdd,
    canEdit,
    canDelete,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    handleToggleStatus,
    handleCreateRole,
    handleViewRole,
    handleEditRole,
    handleDeleteRole,
    closeDeleteModal,
    confirmDeleteRole,
  };
};

export default useAdminCompanyRolesList;
